import { useEffect, useRef, useState } from "react";
import type { PageFilter } from "../lib/pageFilters.js";
import type { DateRangeState } from "../lib/pageFilterInteractions.js";

export function PageFilterDateRange(props: {
  filter: PageFilter;
  state: DateRangeState;
  onChange: (state: DateRangeState) => void;
  onClear: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [localStart, setLocalStart] = useState<string>(props.state.start ?? "");
  const [localEnd, setLocalEnd] = useState<string>(props.state.end ?? "");
  const ref = useRef<HTMLDivElement>(null);
  const { filter, state, onChange, onClear } = props;

  // Sync local inputs when external state changes (e.g., clear all)
  useEffect(() => {
    setLocalStart(state.start ?? "");
    setLocalEnd(state.end ?? "");
  }, [state.start, state.end]);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        // Close panel — if invalid range in progress, clear both
        if (localStart && localEnd && localEnd < localStart) {
          setLocalStart("");
          setLocalEnd("");
          onClear();
        }
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open, localStart, localEnd, onClear]);

  const label = filter.label || "Date Range";
  const hasStart = state.start !== null;
  const hasEnd = state.end !== null;
  const summary = !hasStart && !hasEnd
    ? "Any date"
    : hasStart && hasEnd
      ? `${state.start}–${state.end}`
      : hasStart
        ? `From ${state.start}`
        : `Until ${state.end}`;

  const hasActive = hasStart || hasEnd;

  const inverted = !!(localStart && localEnd && localEnd < localStart);

  function applyChange(start: string, end: string) {
    if (start && end && end < start) return; // hard block — don't propagate invalid range
    onChange({
      start: start || null,
      end: end || null
    });
  }

  function handleStartChange(val: string) {
    setLocalStart(val);
    applyChange(val, localEnd);
  }

  function handleEndChange(val: string) {
    setLocalEnd(val);
    applyChange(localStart, val);
  }

  function handleClear() {
    setLocalStart("");
    setLocalEnd("");
    onClear();
  }

  return (
    <div className="pageFilterDropdown pageFilterDateRange" ref={ref}>
      <button className="pageFilterTrigger" type="button" onClick={() => setOpen(!open)}>
        <span className="pageFilterLabel">{label}</span>
        <span className="pageFilterValue">{summary}</span>
        {hasActive ? (
          <span
            className="pageFilterClearBtn"
            role="button"
            aria-label="Clear date range"
            onClick={(e) => { e.stopPropagation(); handleClear(); }}
          >✕</span>
        ) : (
          <span className="pageFilterArrow">{open ? "▲" : "▼"}</span>
        )}
      </button>
      {open ? (
        <div className="pageFilterPanel pageFilterDatePanel">
          <div className="pageFilterDateInputs">
            <label className="field" style={{ flex: 1 }}>
              <span className="label">Start date</span>
              <input
                className={`input${inverted ? " inputError" : ""}`}
                type="date"
                value={localStart}
                onChange={(e) => handleStartChange(e.target.value)}
              />
            </label>
            <label className="field" style={{ flex: 1 }}>
              <span className="label">End date</span>
              <input
                className={`input${inverted ? " inputError" : ""}`}
                type="date"
                value={localEnd}
                onChange={(e) => handleEndChange(e.target.value)}
              />
            </label>
          </div>
          {inverted ? (
            <div className="pageFilterDateError">End date must be on or after start date.</div>
          ) : null}
          <div className="pageFilterActions" style={{ justifyContent: "flex-end" }}>
            <button className="linkBtn" type="button" onClick={handleClear}>Clear</button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
